// Guests on the folio — primary + companions, each addable by AI ID scan or by hand.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, ScanLine, Trash2, Users } from 'lucide-react';
import { apiCall } from './_shared';
import IdScanPanel from './IdScanPanel';

const FolioGuests = ({ bookingId, onChanged }) => {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState(null); // {first_name,last_name,document_number,nationality,date_of_birth,is_child}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => apiCall(`/api/admin/reception/folios/${bookingId}/guests`).then(setData).catch(() => {}), [bookingId]);
  useEffect(() => { load(); }, [load]);

  const add = async (g) => {
    setBusy(true); setErr('');
    try {
      await apiCall(`/api/admin/reception/folios/${bookingId}/guests`, { method: 'POST', body: JSON.stringify(g) });
      setManual(null); setScanning(false);
      await load(); onChanged?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this companion from the folio?')) return;
    try { await apiCall(`/api/admin/reception/folios/${bookingId}/guests/${id}`, { method: 'DELETE' }); await load(); onChanged?.(); }
    catch (e) { setErr(e.message); }
  };

  const rows = (data?.guests || []).map((g, i) => (
    <li key={g.id || `p-${i}`} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b last:border-0" data-testid={g.is_primary ? 'folio-guest-primary' : `folio-guest-${g.id}`}>
      <span className="text-slate-800">
        <b>{[g.last_name?.toUpperCase(), g.first_name].filter(Boolean).join(' ') || '—'}</b>
        {g.is_primary && <span className="ml-1 px-1 rounded bg-orange-100 text-orange-700 text-[10px] font-semibold">main guest</span>}
        {g.is_child && <span className="ml-1 px-1 rounded bg-sky-100 text-sky-700 text-[10px] font-semibold">child</span>}
        <span className="block text-slate-500">{[g.document_type?.replace('_', ' '), g.document_number, g.nationality, g.date_of_birth && `DOB ${g.date_of_birth}`].filter(Boolean).join(' · ') || 'no document yet'}</span>
      </span>
      {!g.is_primary && <button onClick={() => remove(g.id)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Remove" data-testid={`folio-guest-remove-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
    </li>
  ));

  return (
    <div className="border rounded-lg p-3 bg-white space-y-2" data-testid="folio-guests">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Guests on this folio <span className="text-xs text-slate-400">({data?.guests?.length || 0})</span></h4>
        <div className="flex gap-2">
          <button onClick={() => { setScanning(true); setManual(null); }} className="text-xs font-semibold text-sky-700 hover:underline flex items-center gap-1" data-testid="folio-guest-scan"><ScanLine className="w-3.5 h-3.5" /> Scan companion ID</button>
          <button onClick={() => { setManual({ first_name: '', last_name: '', document_number: '', nationality: '', date_of_birth: '', is_child: false }); setScanning(false); }} className="text-xs font-semibold text-slate-700 hover:underline flex items-center gap-1" data-testid="folio-guest-manual"><Plus className="w-3.5 h-3.5" /> Add by hand</button>
        </div>
      </div>
      <ul>{rows}</ul>
      {scanning && (
        <IdScanPanel bookingId={bookingId} mode="companion" onClose={() => setScanning(false)} onCompanion={(fields) => add({ ...fields, is_child: false })} />
      )}
      {manual && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-slate-50 border rounded p-2" data-testid="folio-guest-manual-form">
          {['first_name', 'last_name', 'document_number', 'nationality', 'date_of_birth'].map((k) => (
            <input key={k} value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} placeholder={k.replace('_', ' ')} className="px-2 py-1 border rounded text-xs" data-testid={`folio-guest-input-${k}`} />
          ))}
          <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={manual.is_child} onChange={(e) => setManual({ ...manual, is_child: e.target.checked })} /> child</label>
          <div className="col-span-full flex gap-2">
            <button onClick={() => add(manual)} disabled={busy} className="px-3 py-1 rounded bg-slate-900 text-white text-xs font-semibold disabled:opacity-50" data-testid="folio-guest-save">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add guest'}</button>
            <button onClick={() => setManual(null)} className="text-xs text-slate-500">Cancel</button>
          </div>
        </div>
      )}
      {err && <p className="text-xs text-red-600" data-testid="folio-guests-error">{err}</p>}
    </div>
  );
};

export default FolioGuests;
